import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  FileText, 
  Download, 
  Calendar, 
  Filter,
  Users,
  UserPlus,
  GraduationCap,
  TrendingUp,
  BarChart3,
  FileSpreadsheet,
  PieChart
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const StaffReports = () => {
  const [selectedReportType, setSelectedReportType] = useState('daily')
  const [selectedFormat, setSelectedFormat] = useState('pdf')
  const [selectedTimeRange, setSelectedTimeRange] = useState('today')
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()

  const reportTypes = [
    {
      id: 'daily',
      title: 'Daily Enrollment Report',
      description: 'New student enrollments for today',
      icon: Calendar,
      count: '12 students',
      color: 'bg-blue-500'
    },
    {
      id: 'weekly',
      title: 'Weekly Enrollment Summary',
      description: 'Week-wise enrollment trends',
      icon: TrendingUp,
      count: '78 students',
      color: 'bg-green-500'
    },
    {
      id: 'monthly',
      title: 'Monthly Enrollment Report',
      description: 'Complete monthly enrollment data',
      icon: BarChart3,
      count: '245 students',
      color: 'bg-purple-500'
    },
    {
      id: 'groupwise',
      title: 'Group-wise Enrollment',
      description: 'Enrollments by course groups',
      icon: PieChart,
      count: '8 groups',
      color: 'bg-orange-500'
    },
    {
      id: 'yearwise',
      title: 'Year-wise Analysis',
      description: 'Academic year comparison',
      icon: GraduationCap,
      count: '4 years',
      color: 'bg-indigo-500'
    },
    {
      id: 'custom',
      title: 'Custom Date Range',
      description: 'Flexible date range reports',
      icon: FileSpreadsheet,
      count: 'Variable',
      color: 'bg-pink-500'
    }
  ]

  const recentReports = [
    {
      id: 1,
      title: 'Daily Enrollment Report - Today',
      type: 'Daily',
      date: new Date().toLocaleDateString(),
      size: '2.4 MB',
      format: 'PDF',
      status: 'Ready'
    },
    {
      id: 2,
      title: 'Weekly Summary - Current Week',
      type: 'Weekly',
      date: new Date(Date.now() - 1000 * 60 * 60 * 24).toLocaleDateString(),
      size: '5.7 MB',
      format: 'Excel',
      status: 'Ready'
    },
    {
      id: 3,
      title: 'Monthly Report - March 2025',
      type: 'Monthly',
      date: new Date(Date.now() - 1000 * 60 * 60 * 48).toLocaleDateString(),
      size: '12.3 MB',
      format: 'PDF',
      status: 'Ready'
    },
    {
      id: 4,
      title: 'Group-wise Analysis - Q1 2025',
      type: 'Group-wise',
      date: new Date(Date.now() - 1000 * 60 * 60 * 72).toLocaleDateString(),
      size: '8.9 MB',
      format: 'Excel',
      status: 'Ready'
    }
  ]

  const quickStats = [
    {
      title: 'Reports Generated Today',
      value: '15',
      description: 'Across all types',
      icon: FileText,
      color: 'text-blue-600'
    },
    {
      title: 'Total Downloads',
      value: '127',
      description: 'This month',
      icon: Download,
      color: 'text-green-600'
    },
    {
      title: 'Automated Reports',
      value: '8',
      description: 'Scheduled daily',
      icon: Calendar,
      color: 'text-purple-600'
    },
    {
      title: 'Data Sources',
      value: '5',
      description: 'Integration points',
      icon: BarChart3,
      color: 'text-orange-600'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ready': return 'bg-green-100 text-green-800'
      case 'Processing': return 'bg-yellow-100 text-yellow-800'
      case 'Failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleGenerateReport = () => {
    // In a real app, this would trigger report generation
    console.log('Generating report:', {
      type: selectedReportType,
      format: selectedFormat,
      timeRange: selectedTimeRange,
      startDate,
      endDate
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-800">Enrollment Reports</h1>
          <p className="text-gray-600 text-sm">Generate and download enrollment reports</p>
        </div>
        <Button className="flex items-center space-x-2">
          <FileText className="h-4 w-4" />
          <span>Schedule Report</span>
        </Button>
      </motion.div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {stat.value}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {stat.description}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Generation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Generate New Report</span>
              </CardTitle>
              <CardDescription>
                Create custom enrollment reports with flexible parameters
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Report Type Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportTypes.map((report) => {
                  const Icon = report.icon
                  const isSelected = selectedReportType === report.id
                  return (
                    <motion.div
                      key={report.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedReportType(report.id)}
                    >
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`w-8 h-8 rounded-lg ${report.color} flex items-center justify-center`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-sm">{report.title}</h3>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{report.description}</p>
                      <Badge variant="outline" className="text-xs">
                        {report.count}
                      </Badge>
                    </motion.div>
                  )
                })}
              </div>

              {/* Report Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Time Range
                  </label>
                  <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="quarter">This Quarter</SelectItem>
                      <SelectItem value="year">This Year</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Output Format
                  </label>
                  <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      <SelectItem value="csv">CSV File</SelectItem>
                      <SelectItem value="json">JSON Data</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button onClick={handleGenerateReport} className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </div>
              </div>

              {/* Custom Date Range */}
              {selectedTimeRange === 'custom' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg"
                >
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Start Date
                    </label>
                    <DatePicker
                      date={startDate}
                      onDateChange={setStartDate}
                      placeholder="Select start date"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      End Date
                    </label>
                    <DatePicker
                      date={endDate}
                      onDateChange={setEndDate}
                      placeholder="Select end date"
                    />
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Reports */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Download className="h-5 w-5" />
                <span>Recent Reports</span>
              </CardTitle>
              <CardDescription>
                Previously generated reports
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentReports.map((report, index) => (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-sm text-gray-900 truncate">
                        {report.title}
                      </h3>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getStatusColor(report.status)}`}
                      >
                        {report.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                      <div>Type: {report.type}</div>
                      <div>Size: {report.size}</div>
                      <div>Date: {report.date}</div>
                      <div>Format: {report.format}</div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full">
                      <Download className="h-3 w-3 mr-2" />
                      Download
                    </Button>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Report Templates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileSpreadsheet className="h-5 w-5" />
              <span>Report Templates</span>
            </CardTitle>
            <CardDescription>
              Pre-configured report templates for common use cases
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-blue-600" />
                  </div>
                  <h3 className="font-semibold">New Enrollments</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Daily report of new student enrollments with details
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Use Template
                </Button>
              </div>

              <div className="p-4 rounded-lg border border-gray-200 hover:border-green-300 transition-colors">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="font-semibold">Trend Analysis</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Weekly/monthly enrollment trends with visualizations
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Use Template
                </Button>
              </div>

              <div className="p-4 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <PieChart className="h-4 w-4 text-purple-600" />
                  </div>
                  <h3 className="font-semibold">Course Distribution</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Student distribution across different course groups
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Use Template
                </Button>
              </div>

              <div className="p-4 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="h-4 w-4 text-orange-600" />
                  </div>
                  <h3 className="font-semibold">Academic Summary</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Comprehensive academic year enrollment summary
                </p>
                <Button size="sm" variant="outline" className="w-full">
                  Use Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

export default StaffReports
